import { Activity, TrendingUp, Gauge, Route, ArrowUpRight, ArrowDownRight, Zap, Fuel } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { fmtMoney } from "@/lib/currency";
import { colorForVehicle } from "@/lib/vehicleColors";

function TrendBadge({ delta, testid }) {
  // Support both legacy number form AND structured {pct, goodWhenUp, label}
  let pct = null;
  let goodWhenUp = true;
  let label = "vs last 7d";
  if (delta && typeof delta === "object") {
    pct = delta.pct;
    if (typeof delta.goodWhenUp === "boolean") goodWhenUp = delta.goodWhenUp;
    if (delta.label) label = delta.label;
    else label = "vs last 30d";
  } else if (typeof delta === "number") {
    pct = delta;
  }

  if (pct === null || pct === undefined || !isFinite(pct)) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] tracking-wider text-[var(--text-faint)] font-mono-data"
        data-testid={testid}
      >
        ─ Need more driving history
      </span>
    );
  }
  if (Math.abs(pct) < 0.5) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] tracking-wider text-[var(--text-muted)] font-mono-data"
        data-testid={testid}
      >
        ─ flat
      </span>
    );
  }
  const up = pct > 0;
  const good = up === goodWhenUp;
  const arrowColor = good ? "text-[#34D17A]" : "text-[#FF5A5F]";
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] tracking-wider font-mono-data text-[var(--text-muted)]"
      data-testid={testid}
    >
      {up ? (
        <ArrowUpRight className={`w-3 h-3 ${arrowColor}`} strokeWidth={2} />
      ) : (
        <ArrowDownRight className={`w-3 h-3 ${arrowColor}`} strokeWidth={2} />
      )}
      <span className="text-[var(--text-primary)]">
        {up ? "+" : ""}
        {pct.toFixed(0)}%
      </span>
      <span>{label}</span>
    </span>
  );
}

function KPICard({ label, icon: Icon, value, sub, testid, tooltip, trendDelta, accent, children, tall = false }) {
  const heightClass = tall
    ? "min-h-[170px] lg:min-h-[200px]"
    : "min-h-[112px] lg:min-h-[124px]";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`group bg-[var(--bg-elevated)] border border-[var(--border-hover)] p-4 lg:p-5 flex flex-col ${heightClass} card-hover relative overflow-hidden cursor-default`}
            style={{ borderRadius: 4 }}
            data-testid={testid}
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-[#E82127]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="flex items-center justify-between">
              <span className="label-tiny">{label}</span>
              <Icon
                className="w-4 h-4 text-[var(--text-muted)] group-hover:text-[#E82127] transition-colors duration-200"
                strokeWidth={1.5}
              />
            </div>
            {children ? (
              <div className="mt-3 flex-1">{children}</div>
            ) : (
              <div className="mt-3">
                <div className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-tighter text-[var(--text-primary)] leading-none">
                  {value}
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {sub && (
                    <span className="text-[11px] text-[var(--text-secondary)] font-mono-data">{sub}</span>
                  )}
                  <TrendBadge delta={trendDelta} testid={`${testid}-trend`} />
                </div>
              </div>
            )}
            {/* bottom accent rail — subtle, fills empty space gracefully */}
            <div className="mt-3">
              <div className="h-px bg-[var(--bg-hover)] relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E82127]/0 via-[#E82127]/40 to-[#E82127]/0 transition-all duration-500 group-hover:via-[#E82127]"
                  style={{ width: `${Math.min(100, Math.max(8, accent || 0))}%` }}
                />
              </div>
            </div>
          </div>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent
            side="top"
            className="bg-[var(--bg-input)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-xs max-w-[280px]"
          >
            {tooltip}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Pick the right "energy kind" (charge / fuel / generic energy) for the
 * spend tile based on the selected vehicle's energy_type, or the dominant
 * cost when viewing the full fleet.
 */
function selectEnergyKind({ energy, vehicles, selectedVehicleId, currencySymbol = "$" }) {
  let kind = "energy";
  if (selectedVehicleId && selectedVehicleId !== "all") {
    const v = vehicles.find((x) => x.id === selectedVehicleId);
    kind = v?.energy_type === "gas" ? "fuel" : v?.energy_type === "ev" ? "charge" : "energy";
  } else if ((energy.fuel_cost || 0) > (energy.charge_cost || 0) && (energy.fuel_cost || 0) > 0) {
    kind = "fuel";
  } else if ((energy.charge_cost || 0) > 0) {
    kind = "charge";
  }
  const spendLabel =
    kind === "fuel" ? "Fuel Spend" : kind === "charge" ? "Charging Spend" : "Energy Spend";
  const SpendIcon = kind === "fuel" ? Fuel : Zap;
  const subCost = energy.subscription_cost || 0;
  const subSuffix = subCost > 0 ? ` · incl. ${currencySymbol}${fmt(subCost, 0)} sub` : "";
  const spendSub =
    kind === "fuel"
      ? `cost · ${fmt(energy.fuel_amount || 0, 0)} L`
      : kind === "charge"
      ? `cost · ${fmt(energy.charge_kwh || 0, 0)} kWh${subSuffix}`
      : "cost in range";
  return { kind, spendLabel, SpendIcon, spendSub };
}

/**
 * Build the adaptive Average-Efficiency tile descriptor — value, sub, tooltip,
 * icon, 30-day trend delta, and accent rail width. Returns hasTile=false when
 * no telemetry exists so the parent can fall back to another KPI.
 */
function buildEfficiencyTile({ stats, unit, energyKind }) {
  const eff = stats?.efficiency || {};
  const evEff = eff.avg_km_per_kwh;
  const fuelEff = eff.avg_l_per_100km;
  const wantsFuel =
    energyKind === "fuel" || (energyKind === "energy" && fuelEff != null && evEff == null);
  const showEv = !wantsFuel && evEff != null;
  const showFuel = wantsFuel && fuelEff != null;
  if (!showEv && !showFuel) {
    return { hasTile: false, accent: 8 };
  }

  // 30-day trend delta (signed %, with direction "goodness" so the badge
  // colours arrows green when efficiency improved).
  const trend = eff.trend || {};
  let trendDelta = null;
  if (showEv && trend.last30_km_per_kwh != null && trend.prev30_km_per_kwh > 0) {
    const pct =
      ((trend.last30_km_per_kwh - trend.prev30_km_per_kwh) / trend.prev30_km_per_kwh) * 100;
    trendDelta = { pct, goodWhenUp: true };
  } else if (showFuel && trend.last30_l_per_100km != null && trend.prev30_l_per_100km > 0) {
    const pct =
      ((trend.last30_l_per_100km - trend.prev30_l_per_100km) / trend.prev30_l_per_100km) * 100;
    trendDelta = { pct, goodWhenUp: false };
  }

  // EV path
  if (showEv) {
    const isMi = unit === "mi";
    const primary = isMi ? (evEff / 1.60934).toFixed(1) : evEff.toFixed(1);
    const whUnit = isMi
      ? eff.avg_wh_per_km != null && `${(eff.avg_wh_per_km * 1.60934).toFixed(0)} Wh/mi`
      : eff.avg_wh_per_km != null && `${eff.avg_wh_per_km.toFixed(0)} Wh/km`;
    return {
      hasTile: true,
      icon: Zap,
      value: primary,
      sub: `${isMi ? "mi" : "km"}/kWh · ${whUnit || ""}`,
      tooltip:
        "Average km per kWh across trips with battery telemetry in the active range. Trend compares last 30 days vs prior 30 days. Higher is better.",
      trendDelta,
      accent: Math.min(100, (evEff / 8) * 100),
    };
  }

  // Fuel path
  const isMi = unit === "mi";
  let value = "—";
  let sub = "L per 100 km · lower is better";
  if (isMi) {
    const mpg = fuelEff > 0 ? 235.215 / fuelEff : null;
    value = mpg != null ? mpg.toFixed(1) : "—";
    sub = `mpg · ${fuelEff.toFixed(1)} L/100km`;
  } else {
    value = fuelEff.toFixed(1);
  }
  return {
    hasTile: true,
    icon: Fuel,
    value,
    sub,
    tooltip:
      "Average fuel consumption across trips with fuel telemetry. Lower is better.",
    trendDelta,
    accent: Math.min(100, (12 / Math.max(fuelEff, 1)) * 100),
  };
}

export default function KPICards({
  stats,
  unit,
  trends,
  currencySymbol = "$",
  vehicles = [],
  selectedVehicleId = "all",
  isMultiAll = false,
  morphCapable = false,
  perVehicleStats = {},
}) {
  const totalKm = stats?.total_km || 0;
  const tripCount = stats?.trip_count || 0;
  const avgPerTripKm = stats?.avg_per_trip_km || 0;
  const avgPerDayKm = stats?.avg_per_day_km || 0;
  const u = unitLabel(unit);

  // Energy aggregates from stats
  const energy = stats?.energy || {};
  const totalEnergyCost = energy.total_cost || 0;
  const costPerKm = energy.cost_per_km || 0;
  const hasEnergyData = (energy.event_count || 0) > 0 || (energy.subscription_cost || 0) > 0;

  const { kind: energyKind, spendLabel, SpendIcon, spendSub } = selectEnergyKind({
    energy,
    vehicles,
    selectedVehicleId,
    currencySymbol,
  });

  const effTile = buildEfficiencyTile({ stats, unit, energyKind });
  const hasEfficiencyTile = effTile.hasTile;

  // Multi-vehicle "All" mode: per-vehicle efficiency (no cross-averaging) +
  // combined spend with a per-vehicle breakdown bar. We render alternate
  // card bodies via the KPICard `children` slot so the grid + outer card
  // dimensions don't change.
  const multi = isMultiAll && vehicles.length > 1;
  const perVehicleEfficiency = multi
    ? vehicles.map((v) => {
        const eff = perVehicleStats[v.id]?.efficiency || {};
        return {
          id: v.id,
          name: v.name,
          energy_type: v.energy_type,
          km_per_kwh: eff.avg_km_per_kwh ?? null,
          l_per_100km: eff.avg_l_per_100km ?? null,
        };
      })
    : [];
  const hasMultiEfficiency =
    multi &&
    perVehicleEfficiency.some(
      (e) => e.km_per_kwh != null || e.l_per_100km != null,
    );

  const perVehicleSpend = multi
    ? vehicles.map((v) => ({
        id: v.id,
        name: v.name,
        cost: perVehicleStats[v.id]?.energy?.total_cost || 0,
      }))
    : [];
  const multiSpendTotal = perVehicleSpend.reduce((a, b) => a + b.cost, 0);
  const hasMultiSpend = multi && multiSpendTotal > 0;

  // Accent rail widths
  const totalCap = stats?.year_total_limit_km || stats?.yearly_limit_km || 20000;
  const accents = {
    total: totalCap > 0 ? Math.min(100, (totalKm / totalCap) * 100) : 30,
    trips: Math.min(100, (tripCount / 60) * 100),
    avgTrip: Math.min(100, (avgPerTripKm / 80) * 100),
    avgDay: Math.min(100, (avgPerDayKm / 90) * 100),
    spend: Math.min(100, (totalEnergyCost / 200) * 100 || 8),
    eff: effTile.accent,
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-cards">
      <KPICard
        label="Total Driven"
        icon={Route}
        value={fmt(fromKm(totalKm, unit), 0)}
        sub={`${u} · in range`}
        testid="kpi-total"
        tooltip="Sum of all trip distances within the active filter (vehicle, usage and date range)."
        trendDelta={trends?.total}
        accent={accents.total}
        tall={morphCapable}
      />
      <KPICard
        label="Trips"
        icon={Activity}
        value={fmt(tripCount, 0)}
        sub="logged entries"
        testid="kpi-trips"
        tooltip="Number of trip records in the active filter range."
        trendDelta={trends?.trips}
        accent={accents.trips}
        tall={morphCapable}
      />
      {hasMultiEfficiency ? (
        <KPICard
          label="Avg Efficiency · Per Vehicle"
          icon={Zap}
          testid="kpi-avg-efficiency"
          tooltip="Each vehicle's efficiency is shown side-by-side — we never average across different energy types."
          accent={accents.eff}
          tall={morphCapable}
        >
          <MultiEfficiencyBody items={perVehicleEfficiency} vehicles={vehicles} unit={unit} />
        </KPICard>
      ) : hasEfficiencyTile ? (
        <KPICard
          label="Avg Efficiency"
          icon={effTile.icon}
          value={effTile.value}
          sub={effTile.sub}
          testid="kpi-avg-efficiency"
          tooltip={effTile.tooltip}
          trendDelta={effTile.trendDelta}
          accent={accents.eff}
          tall={morphCapable}
        />
      ) : hasEnergyData ? (
        <KPICard
          label={spendLabel}
          icon={SpendIcon}
          value={fmtMoney(totalEnergyCost, currencySymbol, 0)}
          sub={spendSub}
          testid="kpi-spend"
          tooltip={`Total ${energyKind === "fuel" ? "fuel" : energyKind === "charge" ? "charging" : "energy"} cost in the active filter range.`}
          accent={accents.spend}
          tall={morphCapable}
        />
      ) : (
        <KPICard
          label="Avg / Trip"
          icon={Gauge}
          value={fmt(fromKm(avgPerTripKm, unit), 1)}
          sub={`${u} per trip`}
          testid="kpi-avg-trip"
          tooltip="Total distance ÷ number of trips."
          trendDelta={trends?.avgPerTrip}
          accent={accents.avgTrip}
          tall={morphCapable}
        />
      )}
      {hasMultiSpend ? (
        <KPICard
          label="Energy Spend · Combined"
          icon={SpendIcon}
          testid="kpi-spend"
          tooltip="Combined fleet cost with a per-vehicle breakdown. Hover each segment to see the vehicle name and amount."
          accent={Math.min(100, (multiSpendTotal / 200) * 100 || 8)}
          tall={morphCapable}
        >
          <MultiSpendBody
            items={perVehicleSpend}
            vehicles={vehicles}
            total={multiSpendTotal}
            currencySymbol={currencySymbol}
          />
        </KPICard>
      ) : hasEnergyData ? (
        <KPICard
          label={hasEfficiencyTile ? spendLabel : "Cost / km"}
          icon={hasEfficiencyTile ? SpendIcon : Gauge}
          value={hasEfficiencyTile ? fmtMoney(totalEnergyCost, currencySymbol, 0) : fmtMoney(costPerKm, currencySymbol, 2)}
          sub={hasEfficiencyTile ? spendSub : `per ${unit === "mi" ? "mi" : "km"} · operating`}
          testid={hasEfficiencyTile ? "kpi-spend" : "kpi-cost-per-km"}
          tooltip={hasEfficiencyTile ? `Total ${energyKind === "fuel" ? "fuel" : "charging"} cost in range.` : "Total energy cost ÷ distance in range. Lower is better."}
          accent={hasEfficiencyTile ? accents.spend : Math.min(100, costPerKm * 20)}
          tall={morphCapable}
        />
      ) : (
        <KPICard
          label="Avg / Day"
          icon={TrendingUp}
          value={fmt(fromKm(avgPerDayKm, unit), 1)}
          sub={`${u} per day`}
          testid="kpi-avg-day"
          tooltip="Total distance ÷ days in range."
          trendDelta={trends?.avgPerDay}
          accent={accents.avgDay}
          tall={morphCapable}
        />
      )}
    </div>
  );
}

/**
 * Multi-vehicle Avg Efficiency body — side-by-side mini bars, each in the
 * vehicle's native units. Bars are scaled relative to the BEST efficient
 * vehicle in the EV cohort and the BEST in the fuel cohort respectively,
 * so the visual is comparable within energy type only.
 */
function MultiEfficiencyBody({ items, vehicles, unit }) {
  const isMi = unit === "mi";
  // Cohorts
  const evs = items.filter((it) => it.km_per_kwh != null);
  const fuels = items.filter((it) => it.l_per_100km != null);
  const evMax = evs.reduce((m, it) => Math.max(m, it.km_per_kwh || 0), 0) || 1;
  const fuelMax = fuels.reduce((m, it) => Math.max(m, it.l_per_100km || 0), 0) || 1;

  return (
    <div className="space-y-2.5" data-testid="kpi-eff-multi">
      {items.map((it) => {
        const c = colorForVehicle(it.id, vehicles);
        const hasEv = it.km_per_kwh != null;
        const hasFuel = it.l_per_100km != null;
        let valueLabel = "—";
        let pct = 0;
        if (hasEv) {
          const v = isMi ? it.km_per_kwh / 1.60934 : it.km_per_kwh;
          valueLabel = `${v.toFixed(1)} ${isMi ? "mi" : "km"}/kWh`;
          pct = (it.km_per_kwh / evMax) * 100;
        } else if (hasFuel) {
          if (isMi) {
            const mpg = it.l_per_100km > 0 ? 235.215 / it.l_per_100km : 0;
            valueLabel = `${mpg.toFixed(1)} mpg`;
          } else {
            valueLabel = `${it.l_per_100km.toFixed(1)} L/100km`;
          }
          pct = (it.l_per_100km / fuelMax) * 100;
        } else {
          valueLabel = "no data";
        }
        return (
          <div key={it.id} data-testid={`kpi-eff-row-${it.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="inline-block w-1.5 h-1.5 shrink-0"
                  style={{ background: c.fill, borderRadius: 1 }}
                />
                <span className="text-[10px] tracking-tight text-[var(--text-primary)] truncate">{it.name}</span>
              </div>
              <span className="text-[10px] font-mono-data text-[var(--text-secondary)]">{valueLabel}</span>
            </div>
            <div className="relative h-1.5 bg-[var(--bg-emphasis)] overflow-hidden" style={{ borderRadius: 1 }}>
              <div
                className="h-full bar-grow"
                style={{
                  width: `${Math.min(100, Math.max(4, pct))}%`,
                  background: hasEv || hasFuel ? c.fill : "#2a2a2a",
                  transition: "width 700ms ease-out",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Multi-vehicle Spend body — combined fleet total on top, with a single
 * horizontal stacked bar underneath whose segments are sized by per-vehicle
 * cost share. Small legend lists each vehicle with its dollar amount.
 */
function MultiSpendBody({ items, vehicles, total, currencySymbol }) {
  return (
    <div data-testid="kpi-spend-multi">
      <div className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-tighter text-[var(--text-primary)] leading-none">
        {fmtMoney(total, currencySymbol, 0)}
      </div>
      <div className="text-[11px] text-[var(--text-secondary)] font-mono-data mt-1.5">
        combined · {items.length} vehicles
      </div>
      <div
        className="mt-2 relative h-2 bg-[var(--bg-emphasis)] overflow-hidden flex"
        style={{ borderRadius: 1 }}
        data-testid="kpi-spend-breakdown-bar"
      >
        {items.map((it) => {
          if (it.cost <= 0) return null;
          const c = colorForVehicle(it.id, vehicles);
          const pct = total > 0 ? (it.cost / total) * 100 : 0;
          return (
            <div
              key={it.id}
              className="h-full bar-grow"
              title={`${it.name}: ${fmtMoney(it.cost, currencySymbol, 2)}`}
              style={{
                width: `${pct}%`,
                background: c.fill,
                transition: "width 700ms ease-out",
              }}
              data-testid={`kpi-spend-segment-${it.id}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {items.map((it) => {
          const c = colorForVehicle(it.id, vehicles);
          return (
            <span key={it.id} className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)]">
              <span
                className="inline-block w-1.5 h-1.5 shrink-0"
                style={{ background: c.fill, borderRadius: 1 }}
              />
              <span className="truncate max-w-[80px]">{it.name}</span>
              <span className="font-mono-data text-[var(--text-primary)]">
                {fmtMoney(it.cost, currencySymbol, 0)}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
